import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import { requiredValidation, richTextValidation } from 'validators';
import { Box, Column, TextField, Tooltip, Typography, WithStatusIcon } from 'seagull';
import { BillingType } from '~/components/BillModal/BillClientModal.types';
import { DiscountTextField, formatDiscountSummary } from '~/components/DiscountTextField';
import { serviceCostModel, serviceModel } from '~/models';
import { utils } from '~/utils';
import { getCellStates, isItemUnselected, MAX_BILL_AMOUNT, validateAmountGreaterThan0, validateRangeRate } from '../../utils/lineItem.utils';
import styles from './columns.module.scss';
import { editableInput, itemDisplayName, itemSelectName, itemSubtitle, lineItemTotal, preapprovedText, rateText, resolveServiceCost } from './columns.utils';
import { DisabledCellTooltip } from './DisabledCellTooltip';
import { ItemServiceSelect } from './ItemServiceSelect/ItemServiceSelect';
import { useOverPreapprovedWarning } from './useOverPreapprovedWarning';
const PrimaryDisplay = (item, rowIndex, params)=>{
    var _ref;
    var _params_itemSelect;
    const { testIds, texts } = params;
    const subtitle = itemSubtitle(item, texts.stageTexts);
    const displayName = item.type === BillingType.Service ? item.fullService.name : itemSelectName(item, (_ref = (_params_itemSelect = params.itemSelect) === null || _params_itemSelect === void 0 ? void 0 : _params_itemSelect.outOfScopeTemplates) !== null && _ref !== void 0 ? _ref : []) || texts.billTexts.addCharge.chooseItemPlaceholder();
    return /*#__PURE__*/ _jsxs(Column, {
        gap: 2,
        children: [
            /*#__PURE__*/ _jsx(Typography, {
                weight: "bold",
                testId: testIds.PrimaryName(rowIndex),
                children: displayName
            }),
            subtitle && /*#__PURE__*/ _jsx(Typography, {
                variant: "paragraph2",
                skin: "secondary",
                testId: testIds.PrimarySubtitle(rowIndex),
                children: subtitle
            })
        ]
    });
};
const DisabledNameCell = (item, rowIndex, params)=>/*#__PURE__*/ _jsx(DisabledCellTooltip, {
        title: params.disabledRowTooltip,
        children: /*#__PURE__*/ _jsx(TextField, {
            bi: {
                name: 'disabled_item_name'
            },
            size: "medium",
            disabled: true,
            testId: params.testIds.RestoredName(rowIndex),
            value: itemDisplayName(item)
        })
    });
const DisabledMoneyCell = (param)=>{
    let { rowIndex, cellKey, value, testId, params } = param;
    return /*#__PURE__*/ _jsx(Box, {
        testId: params.testIds.InputCell(rowIndex, cellKey),
        children: /*#__PURE__*/ _jsx(DisabledCellTooltip, {
            title: params.disabledRowTooltip,
            children: /*#__PURE__*/ _jsx(TextField, {
                bi: {
                    name: "disabled_item_".concat(cellKey)
                },
                size: "medium",
                isMoney: true,
                disabled: true,
                testId: testId,
                value: value
            })
        })
    });
};
const DisabledDiscountCell = (item, rowIndex, params)=>/*#__PURE__*/ _jsx(Box, {
        testId: params.testIds.InputCell(rowIndex, 'discount'),
        children: /*#__PURE__*/ _jsx(DisabledCellTooltip, {
            title: params.disabledRowTooltip,
            children: /*#__PURE__*/ _jsx(DiscountTextField, {
                size: "medium",
                disabled: true,
                testId: params.testIds.RestoredDiscount(rowIndex),
                value: item.charge.discount,
                onChange: ()=>{}
            })
        })
    });
const ItemCell = (item, rowIndex, params)=>{
    const { itemSelect, testIds, texts } = params;
    if (item.disabled) return DisabledNameCell(item, rowIndex, params);
    if (!itemSelect) return PrimaryDisplay(item, rowIndex, params);
    const subtitle = itemSubtitle(item, texts.stageTexts);
    const unselectedError = itemSelect.unselectedItemError != null && isItemUnselected(item);
    return /*#__PURE__*/ _jsxs(Column, {
        gap: 2,
        className: styles.itemSelectCell,
        children: [
            /*#__PURE__*/ _jsx(ItemServiceSelect, {
                rowIndex: rowIndex,
                selectedName: itemSelectName(item, itemSelect.outOfScopeTemplates),
                agreementOptions: itemSelect.agreementOptions,
                outOfScopeTemplates: itemSelect.outOfScopeTemplates,
                formatTemplateCost: itemSelect.formatTemplateCost,
                onSelect: itemSelect.onSelectItem,
                onClear: itemSelect.onClearItem,
                onCreateNew: itemSelect.onCreateNewItem,
                error: unselectedError,
                helperText: unselectedError ? itemSelect.unselectedItemError : undefined,
                testId: testIds.ItemSelect(rowIndex)
            }),
            subtitle && /*#__PURE__*/ _jsx(Typography, {
                variant: "paragraph2",
                skin: "secondary",
                className: styles.itemSelectSubtitle,
                testId: testIds.PrimarySubtitle(rowIndex),
                children: subtitle
            })
        ]
    });
};
const FieldCell = (param)=>{
    let { state, rowIndex, cellKey, input, plain, testIds } = param;
    if (state === 'empty') return null;
    if (state === 'input') {
        return /*#__PURE__*/ _jsx(Box, {
            testId: testIds.InputCell(rowIndex, cellKey),
            children: input
        });
    }
    return /*#__PURE__*/ _jsx(Typography, {
        align: "center",
        testId: testIds.PlainCell(rowIndex, cellKey),
        children: plain
    });
};
export const DescriptionInput = (rowIndex, params, minRows)=>/*#__PURE__*/ _jsx(Controller, {
        control: params.control,
        name: "items.".concat(rowIndex, ".note"),
        rules: richTextValidation('Note'),
        render: (param)=>{
            let { field, fieldState } = param;
            var _field_value;
            return /*#__PURE__*/ _jsx(TextField, {
                bi: {
                    name: 'line_item_note'
                },
                size: "medium",
                multiline: minRows != null,
                minRows: minRows,
                testId: params.testIds.DescriptionInput(rowIndex),
                placeholder: params.texts.billTexts.addDescription(),
                error: !!fieldState.error,
                value: (_field_value = field.value) !== null && _field_value !== void 0 ? _field_value : '',
                onChange: field.onChange,
                onBlur: field.onBlur
            });
        }
    });
const DescriptionCell = (item, rowIndex, params)=>{
    if (item.disabled) {
        return /*#__PURE__*/ _jsx(Box, {
            testId: params.testIds.InputCell(rowIndex, 'description'),
            children: /*#__PURE__*/ _jsx(DisabledCellTooltip, {
                title: params.disabledRowTooltip,
                children: /*#__PURE__*/ _jsx(TextField, {
                    bi: {
                        name: 'disabled_line_item_note'
                    },
                    size: "medium",
                    disabled: true,
                    testId: params.testIds.DescriptionInput(rowIndex),
                    value: item.note
                })
            })
        });
    }
    return /*#__PURE__*/ _jsx(Box, {
        testId: params.testIds.InputCell(rowIndex, 'description'),
        children: DescriptionInput(rowIndex, params)
    });
};
const QuantityField = (param)=>{
    let { name, biName, rowIndex, params, disabled, warning, suffix } = param;
    return /*#__PURE__*/ _jsx(Controller, {
        control: params.control,
        name: name,
        rules: {
            validate: validateAmountGreaterThan0
        },
        render: (param)=>{
            let { field, fieldState } = param;
            var _field_value;
            return /*#__PURE__*/ _jsx(TextField, {
                bi: {
                    name: biName
                },
                testId: params.testIds.QuantityInput(rowIndex),
                size: "medium",
                isMoney: true,
                disabled: disabled,
                error: !!fieldState.error,
                warning: warning && !fieldState.error,
                suffix: suffix,
                value: String((_field_value = field.value) !== null && _field_value !== void 0 ? _field_value : ''),
                onChange: field.onChange,
                onBlur: field.onBlur
            });
        }
    });
};
const ServiceQuantityField = (param)=>{
    let { item, rowIndex, params } = param;
    const isOverPreapproved = useOverPreapprovedWarning(item, rowIndex, params);
    return /*#__PURE__*/ _jsx(Box, {
        children: QuantityField({
            name: "items.".concat(rowIndex, ".service.quantity"),
            biName: 'service_quantity',
            rowIndex,
            params,
            warning: isOverPreapproved,
            suffix: isOverPreapproved ? /*#__PURE__*/ _jsx(Box, {
                testId: params.testIds.QuantityWarning(rowIndex),
                children: /*#__PURE__*/ _jsx(WithStatusIcon, {
                    statusType: "warning",
                    size: "medium",
                    enableOnMobile: true,
                    info: params.texts.billTexts.overPreapprovedTooltip()
                })
            }) : undefined
        })
    });
};
export const QuantityInput = (item, rowIndex, params)=>item.type === BillingType.Service ? /*#__PURE__*/ _jsx(ServiceQuantityField, {
        item: item,
        rowIndex: rowIndex,
        params: params
    }) : QuantityField({
        name: "items.".concat(rowIndex, ".charge.quantity"),
        biName: 'charge_quantity',
        rowIndex,
        params,
        disabled: !item.charge.serviceTemplateId
    });
const QuantityCell = (item, rowIndex, params)=>{
    if (item.disabled) {
        var _item_service_quantity, _item_charge_quantity;
        return DisabledMoneyCell({
            rowIndex,
            cellKey: 'quantity',
            value: item.type === BillingType.Service ? String((_item_service_quantity = item.service.quantity) !== null && _item_service_quantity !== void 0 ? _item_service_quantity : '') : String((_item_charge_quantity = item.charge.quantity) !== null && _item_charge_quantity !== void 0 ? _item_charge_quantity : ''),
            testId: params.testIds.RestoredQuantity(rowIndex),
            params
        });
    }
    const states = getCellStates(item);
    const quantityValue = item.type === BillingType.Service ? item.service.quantity : item.charge.quantity;
    return FieldCell({
        state: states.quantity,
        rowIndex,
        cellKey: 'quantity',
        testIds: params.testIds,
        plain: quantityValue,
        input: QuantityInput(item, rowIndex, params)
    });
};
const ChargeRateInput = (rowIndex, params, disabled)=>/*#__PURE__*/ _jsx(Controller, {
        control: params.control,
        name: "items.".concat(rowIndex, ".charge.rate"),
        rules: requiredValidation(true),
        render: (param)=>{
            let { field, fieldState } = param;
            var _field_value;
            return /*#__PURE__*/ _jsx(TextField, {
                bi: {
                    name: 'charge_rate'
                },
                testId: params.testIds.RateInput(rowIndex),
                size: "medium",
                isMoney: true,
                prefix: "$",
                disabled: disabled,
                error: !!fieldState.error,
                value: String((_field_value = field.value) !== null && _field_value !== void 0 ? _field_value : ''),
                onChange: field.onChange,
                onBlur: field.onBlur
            });
        }
    });
const ServiceRangeRateInput = (item, rowIndex, params)=>{
    const cost = resolveServiceCost(item);
    const maxPrice = serviceCostModel.isRangePrice(cost) ? cost.maxPrice : MAX_BILL_AMOUNT;
    return /*#__PURE__*/ _jsx(Controller, {
        control: params.control,
        name: "items.".concat(rowIndex, ".service.price"),
        rules: {
            validate: validateRangeRate
        },
        render: (param)=>{
            let { field, fieldState } = param;
            var _field_value;
            const isAboveMax = field.value !== '' && field.value != null && maxPrice.lessThan(field.value);
            const showWarning = isAboveMax && !fieldState.error;
            const warningText = params.texts.billTexts.paymentAboveMaxRequiresApproval({
                amount: utils.formatDollarAmount(maxPrice)
            });
            return /*#__PURE__*/ _jsxs(Box, {
                children: [
                    /*#__PURE__*/ _jsx(Tooltip, {
                        title: showWarning ? warningText : '',
                        placement: "top",
                        testId: params.testIds.RateTooltip(rowIndex),
                        children: /*#__PURE__*/ _jsx(Box, {
                            children: /*#__PURE__*/ _jsx(TextField, {
                                bi: {
                                    name: 'service_rate'
                                },
                                testId: params.testIds.RateInput(rowIndex),
                                size: "medium",
                                isMoney: true,
                                prefix: "$",
                                error: !!fieldState.error,
                                warning: showWarning,
                                value: String((_field_value = field.value) !== null && _field_value !== void 0 ? _field_value : ''),
                                onChange: field.onChange,
                                onBlur: field.onBlur
                            })
                        })
                    }),
                    showWarning && /*#__PURE__*/ _jsx(Typography, {
                        variant: "paragraph2",
                        skin: "warning",
                        testId: params.testIds.RateWarning(rowIndex),
                        children: warningText
                    })
                ]
            });
        }
    });
};
export const RateInput = (item, rowIndex, params)=>item.type === BillingType.Service ? ServiceRangeRateInput(item, rowIndex, params) : ChargeRateInput(rowIndex, params, !item.charge.serviceTemplateId);
const RateCell = (item, rowIndex, params)=>{
    const states = getCellStates(item);
    const rateValue = item.type === BillingType.Service ? '' : item.charge.rate;
    if (item.disabled) {
        return DisabledMoneyCell({
            rowIndex,
            cellKey: 'rate',
            value: String(rateValue !== null && rateValue !== void 0 ? rateValue : ''),
            testId: params.testIds.RestoredRate(rowIndex),
            params
        });
    }
    return FieldCell({
        state: states.rate,
        rowIndex,
        cellKey: 'rate',
        testIds: params.testIds,
        plain: item.type === BillingType.Service ? rateText(resolveServiceCost(item), params.texts.costDisplayTexts) : utils.formatDollarAmount(rateValue || 0),
        input: RateInput(item, rowIndex, params)
    });
};
const DiscountField = (param)=>{
    let { item, rowIndex, params } = param;
    const { trigger } = params;
    const rate = useWatch({
        control: params.control,
        name: "items.".concat(rowIndex, ".charge.rate")
    });
    const quantity = useWatch({
        control: params.control,
        name: "items.".concat(rowIndex, ".charge.quantity")
    });
    const discount = useWatch({
        control: params.control,
        name: "items.".concat(rowIndex, ".charge.discount")
    });
    useEffect(()=>{
        trigger("items.".concat(rowIndex, ".charge.discount"));
    }, [
        trigger,
        rowIndex,
        rate,
        quantity,
        discount === null || discount === void 0 ? void 0 : discount.type,
        discount === null || discount === void 0 ? void 0 : discount.amount
    ]);
    return /*#__PURE__*/ _jsx(Controller, {
        control: params.control,
        name: "items.".concat(rowIndex, ".charge.discount"),
        rules: {
            validate: ()=>{
                var _lineItemTotal;
                return ((_lineItemTotal = lineItemTotal(item)) === null || _lineItemTotal === void 0 ? void 0 : _lineItemTotal.lessThan(0)) ? params.texts.billTexts.discountCantExceedRate() : true;
            }
        },
        render: (param)=>{
            let { field, fieldState } = param;
            var _fieldState_error;
            return /*#__PURE__*/ _jsx(DiscountTextField, {
                testId: params.testIds.DiscountInput(rowIndex),
                size: "medium",
                disabled: item.type === BillingType.Charge && !item.charge.serviceTemplateId,
                error: !!fieldState.error,
                helperText: (_fieldState_error = fieldState.error) === null || _fieldState_error === void 0 ? void 0 : _fieldState_error.message,
                value: field.value,
                onChange: field.onChange
            });
        }
    });
};
export const DiscountInput = (item, rowIndex, params)=>/*#__PURE__*/ _jsx(DiscountField, {
        item: item,
        rowIndex: rowIndex,
        params: params
    });
const DiscountCell = (item, rowIndex, params)=>{
    const states = getCellStates(item);
    if (item.disabled) {
        return item.type === BillingType.Charge ? DisabledDiscountCell(item, rowIndex, params) : null;
    }
    if (item.type === BillingType.Service) {
        const discount = serviceModel.discount(item.fullService);
        return FieldCell({
            state: states.discount,
            rowIndex,
            cellKey: 'discount',
            testIds: params.testIds,
            plain: discount ? discount.format() : null,
            input: null
        });
    }
    return FieldCell({
        state: states.discount,
        rowIndex,
        cellKey: 'discount',
        testIds: params.testIds,
        plain: null,
        input: DiscountInput(item, rowIndex, params)
    });
};
const ServicePreapprovedCell = (param)=>{
    let { item, rowIndex, params } = param;
    const isOverPreapproved = useOverPreapprovedWarning(item, rowIndex, params);
    const preapproved = preapprovedText(resolveServiceCost(item));
    if (!isOverPreapproved) {
        return /*#__PURE__*/ _jsx(Typography, {
            align: "center",
            testId: params.testIds.PlainCell(rowIndex, 'preapproved'),
            children: preapproved
        });
    }
    return /*#__PURE__*/ _jsx(Tooltip, {
        title: params.texts.billTexts.overPreapprovedTooltip(),
        placement: "top",
        testId: params.testIds.PreapprovedTooltip(rowIndex),
        children: /*#__PURE__*/ _jsx(Typography, {
            align: "center",
            children: preapproved
        })
    });
};
const PreapprovedCell = (item, rowIndex, params)=>{
    const states = getCellStates(item);
    if (states.preapproved !== 'plain' || item.type !== BillingType.Service) return null;
    return /*#__PURE__*/ _jsx(ServicePreapprovedCell, {
        item: item,
        rowIndex: rowIndex,
        params: params
    });
};
const TotalCell = (item, rowIndex, params)=>{
    const total = lineItemTotal(item);
    return /*#__PURE__*/ _jsx(Typography, {
        align: "center",
        skin: (total === null || total === void 0 ? void 0 : total.lessThan(0)) ? 'error' : 'standard',
        testId: params.testIds.Total(rowIndex),
        children: total != null ? utils.formatDollarAmount(total) : ''
    });
};
const QuantitySummary = (item)=>{
    if (getCellStates(item).quantity !== 'input') return null;
    const value = item.type === BillingType.Service ? item.service.quantity : item.charge.quantity;
    return value != null && value !== '' ? String(value) : null;
};
const RateSummary = (item, params)=>{
    if (item.type === BillingType.Service) return rateText(resolveServiceCost(item), params.texts.costDisplayTexts);
    const rate = item.charge.rate;
    return rate != null && rate !== '' ? utils.formatDollarAmount(rate) : null;
};
const DiscountSummary = (item)=>{
    if (item.type === BillingType.Service) {
        const discount = serviceModel.discount(item.fullService);
        return discount ? discount.format() : null;
    }
    const summary = formatDiscountSummary(item.charge.discount);
    return summary || null;
};
const DescriptionSummary = (item)=>item.note || null;
const PreapprovedSummary = (item)=>item.type === BillingType.Service ? preapprovedText(resolveServiceCost(item)) : null;
const TotalSummary = (item)=>{
    const total = lineItemTotal(item);
    return total != null ? utils.formatDollarAmount(total) : null;
};
export const BuildLineColumns = (params)=>{
    const { texts, includePreapproved } = params;
    const { tableHeaders } = texts;
    const columns = [
        {
            key: 'item',
            header: tableHeaders.item(),
            isPrimary: true,
            alignHeaderToInput: true,
            width: 240,
            renderCell: (item, rowIndex)=>ItemCell(item, rowIndex, params),
            renderSummary: (item, rowIndex)=>PrimaryDisplay(item, rowIndex, params)
        },
        {
            key: 'description',
            header: tableHeaders.description(),
            grow: true,
            maxWidth: 158,
            stacked: true,
            mobileOrder: 50,
            alignHeaderToInput: true,
            renderCell: (item, rowIndex)=>DescriptionCell(item, rowIndex, params),
            renderSummary: (item)=>DescriptionSummary(item),
            renderEdit: (item, rowIndex)=>DescriptionInput(rowIndex, params, 3)
        },
        {
            key: 'quantity',
            header: tableHeaders.quantity(),
            width: 110,
            mobileOrder: 10,
            renderCell: (item, rowIndex)=>QuantityCell(item, rowIndex, params),
            renderSummary: (item)=>QuantitySummary(item),
            renderEdit: (item, rowIndex)=>editableInput(getCellStates(item).quantity, QuantityInput(item, rowIndex, params))
        },
        {
            key: 'rate',
            header: tableHeaders.rate(),
            width: 140,
            mobileOrder: 20,
            renderCell: (item, rowIndex)=>RateCell(item, rowIndex, params),
            renderSummary: (item)=>RateSummary(item, params),
            renderEdit: (item, rowIndex)=>editableInput(getCellStates(item).rate, RateInput(item, rowIndex, params))
        },
        {
            key: 'discount',
            header: tableHeaders.discount(),
            width: 130,
            mobileOrder: 30,
            renderCell: (item, rowIndex)=>DiscountCell(item, rowIndex, params),
            renderSummary: (item)=>DiscountSummary(item),
            renderEdit: (item, rowIndex)=>editableInput(getCellStates(item).discount, DiscountInput(item, rowIndex, params))
        }
    ];
    if (includePreapproved) {
        columns.push({
            key: 'preapproved',
            header: tableHeaders.preapproved(),
            width: 120,
            mobileOrder: 40,
            renderCell: (item, rowIndex)=>PreapprovedCell(item, rowIndex, params),
            renderSummary: (item)=>PreapprovedSummary(item)
        });
    }
    columns.push({
        key: 'total',
        header: tableHeaders.total(),
        width: 100,
        renderCell: (item, rowIndex)=>TotalCell(item, rowIndex, params),
        renderSummary: (item)=>TotalSummary(item),
        dividerBefore: true,
        mobileOrder: 60
    });
    return columns;
};
